import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

type RecorderState = 'idle' | 'recording' | 'recorded' | 'uploading' | 'synthesizing' | 'completed' | 'failed';

interface TranscriptionSegment {
  text?: string;
}

interface VoiceTtsRecord {
  id: number;
  fileName?: string;
  contentType?: string;
  fileSizeBytes?: number;
  durationSeconds?: number;
  recordedAt?: string;
  transcriptionText?: string;
  answerText?: string;
  answerSimilarityScore?: number;
  ttsModel?: string;
  ttsVoice?: string;
  ttsContentType?: string;
  status?: string;
  errorMessage?: string;
  createdAt?: string;
  inputPreviewUrl?: string;
  ttsPreviewUrl?: string;
}

@Component({
  selector: 'app-rnd-text-to-speech',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './rnd-text-to-speech.component.html',
  styleUrls: ['./rnd-text-to-speech.component.scss'],
})
export class RndTextToSpeechComponent implements OnInit, OnDestroy {
  state: RecorderState = 'idle';
  elapsedSeconds = 0;
  audioUrl = '';
  ttsAudioUrl = '';
  mimeType = '';
  fileSize = 0;
  transcription = '';
  answerText = '';
  rawTranscriptionResponse = '';
  rawGetAnswerResponse = '';
  rawTtsResponse = '';
  errorMessage = '';
  showRawTranscription = false;
  showRawAnswer = false;
  showRawTts = false;
  savedRecords: VoiceTtsRecord[] = [];
  recordsLoading = false;

  private mediaStream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private recordedBlob: Blob | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private startedAt = 0;

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.loadSavedRecords();
  }

  get canRecord(): boolean {
    return !['recording', 'uploading', 'synthesizing'].includes(this.state);
  }

  get canStop(): boolean {
    return this.state === 'recording';
  }

  get canSend(): boolean {
    return !!this.recordedBlob && !['recording', 'uploading', 'synthesizing'].includes(this.state);
  }

  get statusLabel(): string {
    switch (this.state) {
      case 'recording': return 'Recording...';
      case 'recorded': return 'Recording ready';
      case 'uploading': return 'Transcribing voice...';
      case 'synthesizing': return 'Getting answer and generating voice...';
      case 'completed': return 'Voice answer ready';
      case 'failed': return 'Action failed';
      default: return 'Ready';
    }
  }

  get formattedDuration(): string {
    const minutes = Math.floor(this.elapsedSeconds / 60).toString().padStart(2, '0');
    const seconds = (this.elapsedSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  }

  get fileSizeLabel(): string {
    return this.fileSizeFor(this.fileSize);
  }

  async startRecording(): Promise<void> {
    if (!this.canRecord) return;

    this.clear(false);
    this.errorMessage = '';

    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });

      this.mimeType = this.pickMimeType();
      this.recorder = new MediaRecorder(
        this.mediaStream,
        this.mimeType ? { mimeType: this.mimeType } : undefined
      );
      this.chunks = [];
      this.startedAt = Date.now();
      this.elapsedSeconds = 0;
      this.state = 'recording';

      this.recorder.addEventListener('dataavailable', event => {
        if (event.data?.size) this.chunks.push(event.data);
      });
      this.recorder.addEventListener('stop', () => this.finalizeRecording());
      this.recorder.start(1000);
      this.timer = setInterval(() => {
        this.elapsedSeconds = Math.floor((Date.now() - this.startedAt) / 1000);
      }, 250);
    } catch (error) {
      this.state = 'failed';
      this.errorMessage = error instanceof Error ? error.message : 'Microphone access failed.';
      this.stopTracks();
    }
  }

  stopRecording(): void {
    if (!this.recorder || this.recorder.state === 'inactive') return;
    this.recorder.stop();
  }

  async sendToBotVoice(): Promise<void> {
    if (!this.recordedBlob) return;

    this.state = 'uploading';
    this.errorMessage = '';
    this.transcription = '';
    this.answerText = '';
    this.rawTranscriptionResponse = '';
    this.rawGetAnswerResponse = '';
    this.rawTtsResponse = '';
    if (this.ttsAudioUrl) URL.revokeObjectURL(this.ttsAudioUrl);
    this.ttsAudioUrl = '';

    try {
      const transcriptionResponse = await this.transcribeRecording();
      this.rawTranscriptionResponse = JSON.stringify(transcriptionResponse, null, 2);
      this.transcription = this.extractTranscription(transcriptionResponse);
      if (!this.transcription || this.transcription === 'No transcription text returned.') {
        throw new Error('Transcription worker did not return usable text.');
      }

      this.state = 'synthesizing';
      const saved = await this.saveVoiceTtsExperiment();
      this.answerText = saved?.answerText || '';
      this.rawGetAnswerResponse = this.prettyJson(saved?.getAnswerResponse);
      this.rawTtsResponse = this.prettyJson(saved?.ttsResponse);
      if (saved?.ttsAudioBase64) {
        const bytes = this.base64ToBytes(saved.ttsAudioBase64);
        const blob = new Blob([bytes], { type: saved.ttsContentType || 'audio/wav' });
        this.ttsAudioUrl = URL.createObjectURL(blob);
      }

      if (saved?.status === 'FAILED') {
        throw new Error(saved?.errorMessage || 'Voice answer generation failed.');
      }

      this.state = 'completed';
      await this.loadSavedRecords();
    } catch (error) {
      this.state = 'failed';
      this.errorMessage = error instanceof Error ? error.message : 'Voice answer flow failed.';
      await this.loadSavedRecords();
    }
  }

  async loadSavedRecords(): Promise<void> {
    this.recordsLoading = true;
    try {
      const response = await firstValueFrom(
        this.http.get<any>(`${this.rndApiBase}/voice-tts?limit=50`)
      );
      this.savedRecords = response?.data?.records || response?.records || [];
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Failed to load saved TTS records.';
    } finally {
      this.recordsLoading = false;
    }
  }

  async previewSavedAudio(record: VoiceTtsRecord, kind: 'input' | 'tts'): Promise<void> {
    const currentUrl = kind === 'input' ? record.inputPreviewUrl : record.ttsPreviewUrl;
    if (currentUrl) {
      URL.revokeObjectURL(currentUrl);
      if (kind === 'input') {
        record.inputPreviewUrl = '';
      } else {
        record.ttsPreviewUrl = '';
      }
      return;
    }

    try {
      const endpoint = kind === 'input' ? 'input-audio' : 'tts-audio';
      const blob = await firstValueFrom(
        this.http.get(`${this.rndApiBase}/voice-tts/${record.id}/${endpoint}`, {
          responseType: 'blob',
        })
      );
      if (kind === 'input') {
        record.inputPreviewUrl = URL.createObjectURL(blob);
      } else {
        record.ttsPreviewUrl = URL.createObjectURL(blob);
      }
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Failed to load saved audio.';
    }
  }

  async deleteSavedRecord(record: VoiceTtsRecord): Promise<void> {
    if (!record.id) return;

    try {
      await firstValueFrom(this.http.delete(`${this.rndApiBase}/voice-tts/${record.id}`));
      if (record.inputPreviewUrl) URL.revokeObjectURL(record.inputPreviewUrl);
      if (record.ttsPreviewUrl) URL.revokeObjectURL(record.ttsPreviewUrl);
      this.savedRecords = this.savedRecords.filter(item => item.id !== record.id);
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Failed to delete saved row.';
    }
  }

  clear(resetState = true): void {
    if (this.recorder && this.recorder.state !== 'inactive') {
      try { this.recorder.stop(); } catch {}
    }
    this.stopTimer();
    this.stopTracks();
    this.recorder = null;
    this.chunks = [];
    this.recordedBlob = null;
    this.fileSize = 0;
    this.mimeType = '';
    this.elapsedSeconds = 0;
    this.transcription = '';
    this.answerText = '';
    this.rawTranscriptionResponse = '';
    this.rawGetAnswerResponse = '';
    this.rawTtsResponse = '';
    this.errorMessage = '';
    this.showRawTranscription = false;
    this.showRawAnswer = false;
    this.showRawTts = false;
    if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
    if (this.ttsAudioUrl) URL.revokeObjectURL(this.ttsAudioUrl);
    this.audioUrl = '';
    this.ttsAudioUrl = '';
    if (resetState) this.state = 'idle';
  }

  ngOnDestroy(): void {
    this.clear(false);
    this.savedRecords.forEach(record => {
      if (record.inputPreviewUrl) URL.revokeObjectURL(record.inputPreviewUrl);
      if (record.ttsPreviewUrl) URL.revokeObjectURL(record.ttsPreviewUrl);
    });
  }

  fileSizeFor(bytes?: number): string {
    if (!bytes) return '-';
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  durationFor(seconds?: number): string {
    if (seconds === undefined || seconds === null) return '-';
    const minutes = Math.floor(seconds / 60).toString().padStart(2, '0');
    const rest = (seconds % 60).toString().padStart(2, '0');
    return `${minutes}:${rest}`;
  }

  confidenceFor(value?: number): string {
    if (value === undefined || value === null) return '-';
    return `${Math.round(value * 100)}%`;
  }

  dateFor(value?: string): string {
    if (!value) return '-';
    return new Date(value).toLocaleString();
  }

  private async transcribeRecording(): Promise<any> {
    if (!this.recordedBlob) return {};

    const channelId = `rnd-tts-${Date.now()}`;
    const form = new FormData();
    form.append('file', this.recordedBlob, `rnd-voice-tts-${channelId}.${this.fileExtension()}`);
    form.append('channelId', channelId);
    form.append('sequenceNo', '1');
    form.append('speakerLabel', 'R&D');
    form.append('startMs', '0');
    form.append('language', environment.transcriptionLanguage || 'ur');

    const response = await fetch(`${environment.transcriptionWorkerUrl}/transcribe/chunk`, {
      method: 'POST',
      body: form,
    });
    const text = await response.text();
    if (!response.ok) {
      throw new Error(`Transcription worker returned HTTP ${response.status}: ${text || response.statusText}`);
    }
    return text ? JSON.parse(text) : {};
  }

  private async saveVoiceTtsExperiment(): Promise<any> {
    if (!this.recordedBlob) return null;

    const form = new FormData();
    form.append('file', this.recordedBlob, `rnd-voice-tts-${Date.now()}.${this.fileExtension()}`);
    form.append('transcriptionText', this.transcription);
    form.append('transcriptionResponse', this.rawTranscriptionResponse);
    form.append('durationSeconds', String(this.elapsedSeconds));
    if (this.startedAt) {
      form.append('recordedAtEpochMs', String(this.startedAt));
    }

    const response = await firstValueFrom(
      this.http.post<any>(`${this.rndApiBase}/voice-tts`, form)
    );
    return response?.data || response;
  }

  private finalizeRecording(): void {
    this.stopTimer();
    this.stopTracks();

    this.recordedBlob = new Blob(this.chunks, { type: this.mimeType || 'audio/webm' });
    this.fileSize = this.recordedBlob.size;
    if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
    this.audioUrl = URL.createObjectURL(this.recordedBlob);
    this.state = this.recordedBlob.size ? 'recorded' : 'failed';
    if (!this.recordedBlob.size) this.errorMessage = 'No audio was captured.';
  }

  private pickMimeType(): string {
    return [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/ogg',
    ].find(type => MediaRecorder.isTypeSupported(type)) || '';
  }

  private fileExtension(): string {
    if (this.mimeType.includes('ogg')) return 'ogg';
    return 'webm';
  }

  private extractTranscription(response: any): string {
    const segments = Array.isArray(response?.segments) ? response.segments as TranscriptionSegment[] : [];
    const fromSegments = segments
      .map(segment => (segment.text || '').trim())
      .filter(Boolean)
      .join('\n');

    return fromSegments || response?.text || response?.transcription || 'No transcription text returned.';
  }

  private base64ToBytes(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  private prettyJson(value?: string): string {
    if (!value) return '';
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }

  private get rndApiBase(): string {
    return `${environment.apiHost.replace(/\/$/, '')}/api/v1/rnd`;
  }

  private stopTracks(): void {
    this.mediaStream?.getTracks().forEach(track => track.stop());
    this.mediaStream = null;
  }

  private stopTimer(): void {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
  }
}
